import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import GridLayout, { useContainerWidth } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./CompFormDesigner.css";
import {
  AlignLeft,
  Braces,
  Calendar,
  CalendarRange,
  CheckSquare,
  ChevronDown,
  CircleDot,
  Code2,
  EyeOff,
  FileImage,
  FileText,
  Frame,
  GripVertical,
  Hash,
  Heading,
  Image,
  Images,
  KeyRound,
  LayoutGrid,
  Link2,
  List,
  ListChecks,
  Minus,
  MousePointerClick,
  Plus,
  Search,
  ShoppingCart,
  Table,
  TextCursorInput,
  ToggleLeft,
  Trash2,
  Type,
  X,
} from "lucide-react";
import GenericForm from "./GenericForm";
import Utils from "../../utils/Utils";
import {
  prepareForm,
  parseFormData,
  clearformData,
  mergeFormData,
} from "../../utils/Functions";

/* Script que GenericForm ejecuta tras cargar, para mostrar/ocultar campos
   condicionales del editor (values, path, iframetype) según el tipo elegido */
const DATAPOSTLOAD = `  let field = formData.type;
    if (field.value==="select"
        || field.value==="radiov"
        || field.value==="radio"
        || field.value==="autocomplete"
        || field.value==="multiple"
        || field.value==="multipleautocomplete"
    ){
        formData.values.type='options';
    }else{
        formData.values.type='hidden';
    }
    if (field.value==="iframe"){
        formData.path.type='text'
        formData.iframetype.type='select'
    }else{
        formData.path.type='space';
        formData.iframetype.type='hidden'
    }
    `;

const FIELD_META = {
  text: { icon: Type, tone: "indigo" },
  numeric: { icon: Hash, tone: "indigo" },
  textarea: { icon: AlignLeft, tone: "indigo" },
  password: { icon: KeyRound, tone: "indigo" },
  hidden: { icon: EyeOff, tone: "slate" },
  select: { icon: ChevronDown, tone: "teal" },
  dropdown: { icon: ChevronDown, tone: "teal" },
  autocomplete: { icon: Search, tone: "teal" },
  multiple: { icon: ListChecks, tone: "teal" },
  multipleautocomplete: { icon: ListChecks, tone: "teal" },
  values: { icon: List, tone: "teal" },
  options: { icon: List, tone: "teal" },
  componentvalues: { icon: Braces, tone: "rose" },
  radio: { icon: CircleDot, tone: "violet" },
  radiov: { icon: CircleDot, tone: "violet" },
  checkbox: { icon: CheckSquare, tone: "violet" },
  switch: { icon: ToggleLeft, tone: "violet" },
  date: { icon: Calendar, tone: "amber" },
  daterange: { icon: CalendarRange, tone: "amber" },
  title: { icon: Heading, tone: "slate" },
  space: { icon: Minus, tone: "slate" },
  iframe: { icon: Frame, tone: "slate" },
  image: { icon: Image, tone: "green" },
  staticimage: { icon: FileImage, tone: "green" },
  multipleimages: { icon: Images, tone: "green" },
  file: { icon: FileText, tone: "green" },
  link: { icon: Link2, tone: "rose" },
  button: { icon: MousePointerClick, tone: "rose" },
  actions: { icon: MousePointerClick, tone: "rose" },
  inputs: { icon: TextCursorInput, tone: "rose" },
  orderitems: { icon: ShoppingCart, tone: "rose" },
  scriptarea: { icon: Code2, tone: "rose" },
  grid: { icon: Table, tone: "rose" },
};

const LABELS = {
  "es-es": {
    title: "Diseñador de formulario",
    fields: "campos",
    field: "campo",
    hint: "Arrastra para reordenar · borde derecho para cambiar el ancho",
    newField: "Nuevo campo",
    editField: "Editar campo",
    empty: "Este formulario aún no tiene campos",
    emptyHint: "Crea el primer campo para empezar a diseñar",
    fieldSize: "Ancho del campo",
    sizes: { 12: "Completo", 6: "Medio", 4: "Pequeño", 2: "Mini" },
    validations: "Validaciones",
    noValidations: "Sin validaciones",
    vRequired: "Requerido",
    vName: "Solo texto (nombre)",
    vNumber: "Numérico",
    vEmail: "Correo electrónico",
    vPhone: "Teléfono",
    edit: "Editar",
    remove: "Eliminar",
    confirmRemove: "¿Eliminar?",
    yes: "Sí",
    no: "No",
    save: "Guardar",
    cancel: "Cancelar",
    clickEdit: "Click para editar el campo",
    selectPlaceholder: "Seleccionar…",
    textPlaceholder: "Texto…",
    longTextPlaceholder: "Texto largo…",
    uploadPlaceholder: "Subir archivo…",
    hiddenField: "campo oculto",
  },
  "en-us": {
    title: "Form designer",
    fields: "fields",
    field: "field",
    hint: "Drag to reorder · right edge to resize",
    newField: "New field",
    editField: "Edit field",
    empty: "This form has no fields yet",
    emptyHint: "Create the first field to start designing",
    fieldSize: "Field width",
    sizes: { 12: "Full", 6: "Medium", 4: "Small", 2: "Mini" },
    validations: "Validations",
    noValidations: "No validations",
    vRequired: "Required",
    vName: "Text only (name)",
    vNumber: "Numeric",
    vEmail: "Email",
    vPhone: "Phone",
    edit: "Edit",
    remove: "Delete",
    confirmRemove: "Delete?",
    yes: "Yes",
    no: "No",
    save: "Save",
    cancel: "Cancel",
    clickEdit: "Click to edit this field",
    selectPlaceholder: "Select…",
    textPlaceholder: "Text…",
    longTextPlaceholder: "Long text…",
    uploadPlaceholder: "Upload file…",
    hiddenField: "hidden field",
  },
};

const SIZE_OPTIONS = [12, 6, 4, 2];

const toInt = (v, def) => {
  const n = parseInt(v, 10);
  return isNaN(n) ? def : n;
};

const layoutIdOf = (field) => field.layout_id || `${field.id}_layout`;

function normalizeFields(value) {
  const arr = Array.isArray(value) ? value : [];
  return arr.map((col, idx) => {
    const id = toInt(col.id, idx + 1);
    return {
      ...col,
      id,
      layout_id: col.layout_id || `${id}_layout`,
      layout_row: toInt(col.layout_row, idx),
      layout_col: toInt(col.layout_col, 0),
      layout_sizeX: Math.min(12, Math.max(2, toInt(col.layout_sizeX, 6))),
      layout_sizeY: 2,
      layout_minSizeY: 2,
      layout_maxSizeY: 2,
    };
  });
}

/* Opciones estáticas de un campo de lista (si las tiene) para el preview */
function staticOptions(field, max) {
  const v = field.values;
  let opts = null;
  if (v && Array.isArray(v.options)) opts = v.options;
  else if (Array.isArray(v)) opts = v;
  if (!opts || opts.length === 0) return null;
  return opts
    .slice(0, max)
    .map((o) => (o && typeof o === "object" ? o.text || o.value : String(o)))
    .filter(Boolean);
}

/* Preview WYSIWYG: cada campo se dibuja como se verá en el formulario real
   (un radio se ve como radio, un check como check, un iframe como marco…).
   Es solo visual (pointer-events: none via CSS) — el click edita el campo. */
function FieldPreview({ field, L }) {
  const t = field.type;
  const label = field.label || field.name || "—";
  const req = field.is_required === "YES";
  const Lbl = () => (
    <span className="xffd-p-label">
      {label}
      {req && <em>*</em>}
    </span>
  );

  switch (t) {
    case "text":
    case "numeric":
    case "autocomplete":
      return (
        <div className="xffd-p">
          <Lbl />
          <div className="xffd-p-input">
            <span>{field.placeholder || (t === "numeric" ? "0" : L.textPlaceholder)}</span>
            {t === "autocomplete" && <Search size={12} />}
          </div>
        </div>
      );
    case "password":
      return (
        <div className="xffd-p">
          <Lbl />
          <div className="xffd-p-input"><span>••••••••</span></div>
        </div>
      );
    case "textarea":
    case "scriptarea":
      return (
        <div className="xffd-p">
          <Lbl />
          <div className="xffd-p-input xffd-p-textarea">
            <span>{t === "scriptarea" ? "// script…" : L.longTextPlaceholder}</span>
          </div>
        </div>
      );
    case "select":
    case "dropdown": {
      const o = staticOptions(field, 1);
      return (
        <div className="xffd-p">
          <Lbl />
          <div className="xffd-p-input xffd-p-select">
            <span>{o ? o[0] : L.selectPlaceholder}</span>
            <ChevronDown size={13} />
          </div>
        </div>
      );
    }
    case "multiple":
    case "multipleautocomplete": {
      const o = staticOptions(field, 2) || ["A", "B"];
      return (
        <div className="xffd-p">
          <Lbl />
          <div className="xffd-p-input xffd-p-select">
            <span className="xffd-p-tags">
              {o.map((x, i) => (
                <i key={i}>{x}</i>
              ))}
            </span>
            <ChevronDown size={13} />
          </div>
        </div>
      );
    }
    case "radio":
    case "radiov": {
      const o = staticOptions(field, 3) || ["Opción 1", "Opción 2"];
      return (
        <div className="xffd-p">
          <Lbl />
          <div className={"xffd-p-radios" + (t === "radiov" ? " xffd-p-vert" : "")}>
            {o.map((x, i) => (
              <span key={i} className="xffd-p-radio">
                <i data-on={i === 0 ? "1" : undefined} />
                {x}
              </span>
            ))}
          </div>
        </div>
      );
    }
    case "checkbox":
      return (
        <div className="xffd-p xffd-p-middle">
          <span className="xffd-p-check" />
          <span className="xffd-p-label xffd-p-inlbl">
            {label}
            {req && <em>*</em>}
          </span>
        </div>
      );
    case "switch":
      return (
        <div className="xffd-p xffd-p-middle">
          <span className="xffd-p-switch"><i /></span>
          <span className="xffd-p-label xffd-p-inlbl">
            {label}
            {req && <em>*</em>}
          </span>
        </div>
      );
    case "date":
      return (
        <div className="xffd-p">
          <Lbl />
          <div className="xffd-p-input xffd-p-select">
            <span>dd/mm/aaaa</span>
            <Calendar size={13} />
          </div>
        </div>
      );
    case "daterange":
      return (
        <div className="xffd-p">
          <Lbl />
          <div className="xffd-p-input xffd-p-select">
            <span>dd/mm — dd/mm</span>
            <CalendarRange size={13} />
          </div>
        </div>
      );
    case "title":
      return (
        <div className="xffd-p xffd-p-middle">
          <span className="xffd-p-title">{label}</span>
        </div>
      );
    case "space":
      return (
        <div className="xffd-p xffd-p-middle">
          <span className="xffd-p-space" />
        </div>
      );
    case "iframe":
      return (
        <div className="xffd-p">
          <Lbl />
          <div className="xffd-p-frame">
            <span className="xffd-p-frame-bar">
              <i /><i /><i />
              <b>{field.path || "https://…"}</b>
            </span>
            <span className="xffd-p-frame-body">
              <Frame size={14} />
            </span>
          </div>
        </div>
      );
    case "image":
    case "staticimage":
      return (
        <div className="xffd-p">
          <Lbl />
          <div className="xffd-p-media"><Image size={16} /></div>
        </div>
      );
    case "multipleimages":
      return (
        <div className="xffd-p">
          <Lbl />
          <div className="xffd-p-media xffd-p-media-multi">
            <Images size={16} /><i /><i />
          </div>
        </div>
      );
    case "file":
      return (
        <div className="xffd-p">
          <Lbl />
          <div className="xffd-p-media xffd-p-file">
            <FileText size={14} />
            <span>{L.uploadPlaceholder}</span>
          </div>
        </div>
      );
    case "button":
    case "actions":
      return (
        <div className="xffd-p xffd-p-middle">
          <span className="xffd-p-btn">{label}</span>
        </div>
      );
    case "link":
      return (
        <div className="xffd-p xffd-p-middle">
          <span className="xffd-p-link">
            <Link2 size={13} />
            {label}
          </span>
        </div>
      );
    case "hidden":
      return (
        <div className="xffd-p xffd-p-middle">
          <span className="xffd-p-hidden">
            <EyeOff size={13} />
            {field.name} · {L.hiddenField}
          </span>
        </div>
      );
    default: {
      const meta = FIELD_META[t] || { icon: TextCursorInput };
      const Icon = meta.icon;
      return (
        <div className="xffd-p xffd-p-middle">
          <span className="xffd-p-generic">
            <Icon size={14} />
            <b>{t || "?"}</b>
            {label}
          </span>
        </div>
      );
    }
  }
}

function validationChips(field, L) {
  const chips = [];
  if (field.is_required === "YES")
    chips.push({ key: "req", text: "R", title: L.vRequired, tone: "rose" });
  if (field.is_name === "YES")
    chips.push({ key: "name", text: "Aa", title: L.vName, tone: "teal" });
  if (field.is_number === "YES")
    chips.push({ key: "num", text: "123", title: L.vNumber, tone: "indigo" });
  if (field.is_email === "YES" || field.is_mail === "YES")
    chips.push({ key: "mail", text: "@", title: L.vEmail, tone: "amber" });
  if (field.is_phonenumber === "YES")
    chips.push({ key: "tel", text: "Tel", title: L.vPhone, tone: "violet" });
  return chips;
}

function CompFormDesinger(props) {
  const { name, label, value, onChange, defConfig } = props;
  const lang = props.defLang && Utils[props.defLang] ? props.defLang : "es-es";
  const L = LABELS[lang] || LABELS["es-es"];

  const [fields, setFields] = useState(() => normalizeFields(value));
  const [editor, setEditor] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const countRef = useRef(
    normalizeFields(value).reduce((m, f) => Math.max(m, f.id), 0)
  );
  /* Click-para-editar sin pelearse con el drag de react-grid-layout: se
     guarda dónde bajó el mouse y solo se abre el editor si el click terminó
     prácticamente en el mismo punto (un drag real recorre distancia). */
  const pressRef = useRef(null);
  const baseFormRef = useRef(null);
  const editorFieldsRef = useRef([]);
  const editorSeqRef = useRef(0);
  const layoutAbsorbedRef = useRef(false);

  const { width, containerRef, mounted } = useContainerWidth();

  useEffect(() => {
    let alive = true;
    (async () => {
      const dict = Utils[lang] || Utils["es-es"];
      const ff = await prepareForm(dict.formFieldEditor);
      if (alive) baseFormRef.current = ff;
    })();
    return () => {
      alive = false;
    };
  }, [lang]);

  const emit = useCallback(
    (nextFields) => {
      const values = nextFields
        .map((f) => ({
          ...f,
          sortorder: f.layout_row * 100 + f.layout_col,
        }))
        .sort((a, b) => a.sortorder - b.sortorder);
      onChange({ target: { name, value: values } });
    },
    [name, onChange]
  );

  const layout = useMemo(
    () =>
      fields.map((f) => {
        const w = Math.min(12, Math.max(2, toInt(f.layout_sizeX, 6)));
        return {
          i: layoutIdOf(f),
          x: Math.min(12 - w, Math.max(0, toInt(f.layout_col, 0))),
          y: Math.max(0, toInt(f.layout_row, 0)),
          w,
          h: 2,
          minW: 2,
          maxW: 12,
          minH: 2,
          maxH: 2,
        };
      }),
    [fields]
  );

  const handleLayoutChange = useCallback(
    (nextLayout) => {
      let changed = false;
      const next = fields.map((f) => {
        const it = nextLayout.find((l) => l.i === layoutIdOf(f));
        if (!it) return f;
        if (
          it.x !== f.layout_col ||
          it.y !== f.layout_row ||
          it.w !== f.layout_sizeX
        ) {
          changed = true;
          return {
            ...f,
            layout_col: it.x,
            layout_row: it.y,
            layout_sizeX: it.w,
            layout_sizeY: 2,
          };
        }
        return f;
      });
      if (!changed) return;
      setFields(next);
      /* La primera compactación (al montar, sobre datos legacy con filas
         dispersas) solo se absorbe en el estado; no cuenta como edición */
      if (!layoutAbsorbedRef.current) {
        layoutAbsorbedRef.current = true;
        return;
      }
      emit(next);
    },
    [fields, emit]
  );

  const ensureBaseForm = useCallback(async () => {
    if (!baseFormRef.current) {
      const dict = Utils[lang] || Utils["es-es"];
      baseFormRef.current = await prepareForm(dict.formFieldEditor);
    }
    return baseFormRef.current;
  }, [lang]);

  const openCreate = useCallback(async () => {
    const base = await ensureBaseForm();
    const formFields = clearformData(base);
    editorFieldsRef.current = formFields;
    editorSeqRef.current += 1;
    setEditor({
      mode: "create",
      fieldId: null,
      sizeCol: 6,
      formFields,
      seq: editorSeqRef.current,
    });
  }, [ensureBaseForm]);

  const openEdit = useCallback(
    async (field) => {
      const base = await ensureBaseForm();
      const formFields = mergeFormData(clearformData(base), field);
      editorFieldsRef.current = formFields;
      editorSeqRef.current += 1;
      setEditor({
        mode: "update",
        fieldId: field.id,
        sizeCol: Math.min(12, Math.max(2, toInt(field.layout_sizeX, 6))),
        formFields,
        seq: editorSeqRef.current,
      });
    },
    [ensureBaseForm]
  );

  /* Mismo contrato de eventos que el editor anterior: GenericForm emite
     {name, value} planos, {type:'switch'} para toggles y {type:'options'}
     con el detalle estático/API para listas de opciones */
  const handleEditorChange = useCallback((event) => {
    editorFieldsRef.current.forEach((a) => {
      if (a["name"] !== event.target.name) return;
      if (event.target.type === "switch") {
        a["value"] = a["value"] === "YES" ? "NO" : "YES";
      } else if (event.target.type === "options") {
        const data = event.target;
        if (data.datatype === "static") {
          a["value"] = { datatype: data.datatype, options: data.options };
        } else {
          a["value"] = {
            datatype: data.datatype,
            apidata: data.apidata,
            apimethod: data.apimethod,
            apitoken: data.apitoken,
            apiurl: data.apiurl,
            text: data.text,
            value: data.value,
          };
        }
      } else if (!event.target.type) {
        a["value"] = event.target.value;
      } else {
        a["value"] = event.target.value;
      }
    });
  }, []);

  const closeEditor = useCallback(() => setEditor(null), []);

  const saveField = useCallback(() => {
    if (!editor) return;
    const formData = parseFormData(editorFieldsRef.current);
    let next;
    if (editor.mode === "update") {
      next = fields.map((f) =>
        f.id === editor.fieldId
          ? {
              ...formData,
              id: f.id,
              layout_id: f.layout_id,
              layout_row: f.layout_row,
              layout_col: f.layout_col,
              layout_sizeX: editor.sizeCol,
              layout_sizeY: 2,
              layout_minSizeY: 2,
              layout_maxSizeY: 2,
            }
          : f
      );
    } else {
      const id = countRef.current + 1;
      countRef.current = id;
      const bottom = fields.reduce(
        (m, f) => Math.max(m, toInt(f.layout_row, 0) + 2),
        0
      );
      next = [
        ...fields,
        {
          ...formData,
          id,
          layout_id: `${id}_layout`,
          layout_row: bottom,
          layout_col: 0,
          layout_sizeX: editor.sizeCol,
          layout_sizeY: 2,
          layout_minSizeY: 2,
          layout_maxSizeY: 2,
        },
      ];
    }
    setFields(next);
    emit(next);
    setEditor(null);
  }, [editor, fields, emit]);

  const deleteField = useCallback(
    (id) => {
      const next = fields.filter((f) => f.id !== id);
      setFields(next);
      emit(next);
      setPendingDelete(null);
    },
    [fields, emit]
  );

  useEffect(() => {
    if (!editor) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setEditor(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [editor]);

  const dialogTitle =
    (defConfig && defConfig[lang] && defConfig[lang].fieldinfo) ||
    (editor && editor.mode === "update" ? L.editField : L.newField);

  return (
    <div className="xffd">
      <div className="xffd-head">
        <div className="xffd-head-left">
          <span className="xffd-head-icon">
            <LayoutGrid size={18} />
          </span>
          <div>
            <div className="xffd-title">{label || L.title}</div>
            <div className="xffd-sub">
              {fields.length} {L.fields} · {L.hint}
            </div>
          </div>
        </div>
        <button
          type="button"
          className="xffd-btn xffd-btn-primary"
          onClick={openCreate}
        >
          <Plus size={15} />
          <span>{L.newField}</span>
        </button>
      </div>

      <div className="xffd-canvas" ref={containerRef}>
        {fields.length === 0 ? (
          <div className="xffd-empty">
            <span className="xffd-empty-icon">
              <LayoutGrid size={26} />
            </span>
            <div className="xffd-empty-title">{L.empty}</div>
            <div className="xffd-empty-hint">{L.emptyHint}</div>
            <button
              type="button"
              className="xffd-btn xffd-btn-primary"
              onClick={openCreate}
            >
              <Plus size={15} />
              <span>{L.newField}</span>
            </button>
          </div>
        ) : (
          mounted && (
            <GridLayout
              layout={layout}
              width={width}
              gridConfig={{ cols: 12, rowHeight: 56, margin: [10, 10] }}
              dragConfig={{ cancel: ".xffd-no-drag" }}
              resizeConfig={{ handles: ["e"] }}
              onLayoutChange={handleLayoutChange}
            >
              {fields.map((f) => {
                const meta = FIELD_META[f.type] || {
                  icon: TextCursorInput,
                  tone: "slate",
                };
                const Icon = meta.icon;
                const chips = validationChips(f, L);
                return (
                  <div key={layoutIdOf(f)} className="xffd-item">
                    <div
                      className={`xffd-card xffd-card-wysiwyg xffd-tone-${meta.tone}`}
                      data-size={
                        f.layout_sizeX <= 3
                          ? "xs"
                          : f.layout_sizeX <= 5
                          ? "sm"
                          : "md"
                      }
                      title={L.clickEdit}
                      onMouseDownCapture={(e) => {
                        pressRef.current = { x: e.clientX, y: e.clientY };
                      }}
                      onClick={(e) => {
                        const p = pressRef.current;
                        if (
                          p &&
                          Math.abs(e.clientX - p.x) < 6 &&
                          Math.abs(e.clientY - p.y) < 6
                        ) {
                          openEdit(f);
                        }
                      }}
                    >
                      <span className="xffd-accent" />
                      <div className="xffd-strip">
                        <span className="xffd-grip">
                          <GripVertical size={13} />
                        </span>
                        <span className="xffd-strip-icon">
                          <Icon size={12} />
                        </span>
                        <span className="xffd-name" title={f.name}>
                          {f.name || "—"}
                        </span>
                        <span className="xffd-type">{f.type || "?"}</span>
                        <span className="xffd-chips" title={L.validations}>
                          {chips.map((c) => (
                            <span
                              key={c.key}
                              className={`xffd-chip xffd-chip-${c.tone}`}
                              title={c.title}
                            >
                              {c.text}
                            </span>
                          ))}
                        </span>
                        <span className="xffd-width">{f.layout_sizeX}/12</span>
                        <div
                          className="xffd-actions xffd-no-drag"
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          {pendingDelete === f.id ? (
                            <span className="xffd-confirm">
                              <span className="xffd-confirm-text">
                                {L.confirmRemove}
                              </span>
                              <button
                                type="button"
                                className="xffd-icon-btn xffd-icon-btn-danger"
                                title={L.yes}
                                onClick={() => deleteField(f.id)}
                              >
                                {L.yes}
                              </button>
                              <button
                                type="button"
                                className="xffd-icon-btn"
                                title={L.no}
                                onClick={() => setPendingDelete(null)}
                              >
                                {L.no}
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="xffd-icon-btn xffd-icon-btn-danger"
                              title={L.remove}
                              onClick={() => setPendingDelete(f.id)}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                      <FieldPreview field={f} L={L} />
                    </div>
                  </div>
                );
              })}
            </GridLayout>
          )
        )}
        {fields.length > 0 && (
          <button
            type="button"
            className="xffd-addtile"
            onClick={openCreate}
            title={L.newField}
          >
            <Plus size={15} />
            <span>{L.newField}</span>
          </button>
        )}
      </div>

      {editor && (
        <div
          className="xffd-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeEditor();
          }}
        >
          <div className="xffd-modal" role="dialog" aria-modal="true">
            <div className="xffd-modal-head">
              <div className="xffd-modal-title">{dialogTitle}</div>
              <button
                type="button"
                className="xffd-icon-btn"
                title={L.cancel}
                onClick={closeEditor}
              >
                <X size={16} />
              </button>
            </div>
            <div className="xffd-sizes">
              <span className="xffd-sizes-label">{L.fieldSize}</span>
              <div className="xffd-sizes-options">
                {SIZE_OPTIONS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={`xffd-size ${
                      editor.sizeCol === v ? "xffd-size-active" : ""
                    }`}
                    onClick={() => setEditor({ ...editor, sizeCol: v })}
                  >
                    <span className="xffd-size-bar">
                      <i style={{ width: `${(v / 12) * 100}%` }} />
                    </span>
                    <span className="xffd-size-name">{L.sizes[v]}</span>
                    <span className="xffd-size-cols">{v}/12</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="xffd-modal-body">
              <GenericForm
                key={editor.seq}
                data={editor.formFields}
                datapostload={DATAPOSTLOAD}
                onChange={handleEditorChange}
                buttons={[]}
              />
            </div>
            <div className="xffd-modal-foot">
              <button
                type="button"
                className="xffd-btn xffd-btn-ghost"
                onClick={closeEditor}
              >
                {L.cancel}
              </button>
              <button
                type="button"
                className="xffd-btn xffd-btn-primary"
                onClick={saveField}
              >
                {L.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompFormDesinger;
